<template>
    <v-container fluid class="pa-4">
        <div class="d-flex align-center mb-4 ga-3">
            <v-icon icon="mdi-history" color="primary" size="28"/>
            <h1 class="text-h5 font-weight-bold">Activity</h1>
            <v-spacer/>
            <v-chip v-if="unreadCount > 0" color="error" size="small" prepend-icon="mdi-bell-badge">
                {{ unreadCount }} unread
            </v-chip>
            <v-btn icon="mdi-refresh" variant="text" size="small" aria-label="Refresh"
                   :loading="loading || feedLoading"
                   @click="tab === 'feed' ? refreshFeed() : refresh()"/>
            <v-menu>
                <template #activator="{ props: mp }">
                    <v-btn v-bind="mp" icon="mdi-dots-vertical" variant="text" size="small" aria-label="More actions"/>
                </template>
                <v-list density="compact">
                    <v-list-item prepend-icon="mdi-email-open-outline" title="Mark all read"
                                 @click="bulkAction = 'read'"/>
                    <v-list-item prepend-icon="mdi-delete-sweep-outline" title="Clear all"
                                 @click="bulkAction = 'clear'"/>
                </v-list>
            </v-menu>
        </div>

        <v-card rounded="lg" elevation="0" border>
            <v-tabs v-model="tab" color="primary" class="border-b">
                <v-tab value="alerts" prepend-icon="mdi-bell-outline">Alerts</v-tab>
                <v-tab value="feed" prepend-icon="mdi-timeline-outline">Feed</v-tab>
            </v-tabs>

            <v-window v-model="tab">
                <!-- ═══════════════════════════════════════ ALERTS TAB ═══════════════════════════════════════ -->
                <v-window-item value="alerts" eager>
                    <!-- Type filter chips (hidden while the needs-attention view, which ignores them, is on) -->
                    <div v-if="!attentionOnly" class="px-4 pt-3 pb-1">
                        <div class="text-caption text-medium-emphasis mb-1">Alert type</div>
                        <v-chip-group v-model="activeTypeFilter" mandatory class="py-0">
                            <v-chip value="" variant="tonal" size="small"
                                    :color="activeTypeFilter === '' ? 'primary' : undefined">
                                All types
                            </v-chip>
                            <v-chip v-for="t in ALL_TYPES" :key="t.value" :value="t.value"
                                    :prepend-icon="t.icon"
                                    variant="tonal" size="small"
                                    :color="activeTypeFilter === t.value ? t.color : undefined">
                                {{ t.label }}
                            </v-chip>
                        </v-chip-group>
                    </div>

                    <!-- Vehicle filter chips -->
                    <div v-if="assets.length > 1 && !attentionOnly" class="px-4 pb-2">
                        <div class="text-caption text-medium-emphasis mb-1">Vehicle</div>
                        <v-chip-group v-model="activeAssetFilter" mandatory class="py-0">
                            <v-chip value="" variant="tonal" size="small"
                                    :color="activeAssetFilter === '' ? 'primary' : undefined">
                                All vehicles
                            </v-chip>
                            <v-chip v-for="a in assets" :key="a.id" :value="a.id"
                                    prepend-icon="mdi-truck" variant="tonal" size="small"
                                    :color="activeAssetFilter === a.id ? 'primary' : undefined">
                                {{ a.name }}
                            </v-chip>
                        </v-chip-group>
                    </div>

                    <v-divider/>

                    <!-- Needs attention: emergencies, urgent alerts and low fuel from the last 48 hours -->
                    <div v-if="attentionItems.length && !attentionOnly" class="px-4 pt-4">
                        <v-card variant="outlined" rounded="lg" class="attention-strip" role="region"
                                aria-label="Needs attention">
                            <v-card-text class="pa-3">
                                <div class="d-flex align-center ga-2 mb-2">
                                    <v-icon icon="mdi-alert-circle" color="error" size="18"/>
                                    <span class="text-caption font-weight-black text-uppercase letter-spacing-wide text-error">
                                        Needs attention
                                    </span>
                                    <v-chip size="x-small" color="error" variant="flat">{{ attentionItems.length }}</v-chip>
                                    <span class="text-caption text-medium-emphasis">Last 48 hours</span>
                                </div>
                                <div v-for="a in attentionItems.slice(0, 3)" :key="'att-' + a.id"
                                     class="d-flex align-center ga-2 py-1">
                                    <v-icon :icon="alertIcon(a)" :color="alertColor(a)" size="16" class="flex-shrink-0"/>
                                    <span class="text-body-2 font-weight-medium text-truncate flex-grow-1 min-w-0">
                                        {{ alertHeadline(a) || a.title }}
                                    </span>
                                    <AlertSeverityChip :alert="a"/>
                                    <span class="text-caption text-medium-emphasis flex-shrink-0">{{ agoOf(a) }}</span>
                                </div>
                                <v-btn variant="text" size="small" color="error" append-icon="mdi-chevron-right"
                                       class="mt-1 px-1" @click="attentionOnly = true">
                                    View all {{ attentionItems.length }}
                                </v-btn>
                            </v-card-text>
                        </v-card>
                    </div>

                    <v-alert v-if="attentionOnly" type="error" variant="tonal" density="compact"
                             class="mx-4 mt-4 mb-0" icon="mdi-alert-circle-outline">
                        <div class="d-flex align-center flex-wrap ga-2">
                            <span class="text-body-2">
                                Showing {{ attentionItems.length }} {{ attentionItems.length === 1 ? 'alert' : 'alerts' }}
                                that need attention from the last 48 hours
                            </span>
                            <v-spacer/>
                            <v-btn size="small" variant="text" @click="attentionOnly = false">Show all alerts</v-btn>
                        </div>
                    </v-alert>

                    <!-- Loading skeleton -->
                    <div v-if="loading && !alerts.length && !attentionOnly" class="pa-4">
                        <v-skeleton-loader v-for="i in 6" :key="i" type="list-item-avatar-two-line" class="mb-2"/>
                    </div>

                    <!-- Empty state -->
                    <div v-else-if="!loading && !displayedAlerts.length"
                         class="d-flex flex-column align-center pa-12 text-center">
                        <v-icon icon="mdi-bell-sleep-outline" size="64" color="grey-lighten-2"/>
                        <p class="text-body-1 text-medium-emphasis mt-3">No alerts found</p>
                        <p v-if="activeTypeFilter || activeAssetFilter" class="text-caption text-medium-emphasis">
                            Try removing the active filter
                        </p>
                        <v-btn v-if="activeTypeFilter || activeAssetFilter"
                               variant="tonal" size="small" class="mt-3" @click="clearFilters">
                            Clear filters
                        </v-btn>
                    </div>

                    <!-- Day-grouped alert feed -->
                    <div v-else class="pa-4">
                        <template v-for="group in groupedAlerts" :key="group.key">
                            <div class="text-caption font-weight-bold text-medium-emphasis text-uppercase letter-spacing-wide mb-2 mt-4 first-group">
                                {{ group.label }}
                                <span class="ms-1">({{ group.items.length }})</span>
                            </div>
                            <ActivityAlertCard
                                v-for="alert in group.items"
                                :key="alert.id"
                                :notification="alert"
                                :now-tick="nowTick"
                                :address="alertAddressOf(alert)"
                                :is-new="newIds.has(alert.id)"
                                @toggle-read="toggleRead"
                            />
                        </template>
                    </div>

                    <!-- Load more -->
                    <div v-if="hasMore && !loading" class="d-flex justify-center pa-4 pt-2">
                        <v-btn variant="tonal" color="primary" size="small"
                               :loading="loadingMore" @click="loadMore">
                            Load more alerts
                        </v-btn>
                    </div>
                </v-window-item>

                <!-- ═══════════════════════════════════════ FEED TAB ═══════════════════════════════════════ -->
                <v-window-item value="feed" eager>
                    <!-- Summary card -->
                    <div v-if="feedItems.length || feedTrips.length" class="pa-4 pb-0">
                        <FeedSummaryCard :trips="assetFeedTrips" :alerts="assetFeedAlerts"
                                         :trips-partial="feedTripsCut" :alerts-partial="feedAlertsCut"/>
                    </div>

                    <!-- Vehicle filter -->
                    <div v-if="assets.length > 1" class="px-4 pt-3 pb-1">
                        <div class="text-caption text-medium-emphasis mb-1">Vehicle</div>
                        <v-chip-group v-model="feedAssetFilter" mandatory class="py-0">
                            <v-chip value="" variant="tonal" size="small"
                                    :color="feedAssetFilter === '' ? 'primary' : undefined">
                                All vehicles
                            </v-chip>
                            <v-chip v-for="a in assets" :key="a.id" :value="a.id"
                                    prepend-icon="mdi-truck" variant="tonal" size="small"
                                    :color="feedAssetFilter === a.id ? 'primary' : undefined">
                                {{ a.name }}
                            </v-chip>
                        </v-chip-group>
                    </div>
                    <v-divider v-if="assets.length > 1"/>

                    <!-- Loading -->
                    <div v-if="feedLoading && !feedItems.length && !liveTrips.length" class="pa-4">
                        <v-skeleton-loader v-for="i in 4" :key="i" type="card" class="mb-3"/>
                    </div>

                    <!-- Trips failed to load -->
                    <v-alert v-if="feedFailure && !feedLoading" type="error" variant="tonal" density="compact"
                             class="ma-4">
                        {{ feedFailure }}
                        <template #append>
                            <v-btn size="small" variant="text" @click="refreshFeed">Retry</v-btn>
                        </template>
                    </v-alert>

                    <!-- Empty -->
                    <div v-if="!feedLoading && !feedFailure && !feedItems.length && !liveTrips.length"
                         class="d-flex flex-column align-center pa-12 text-center">
                        <v-icon icon="mdi-timeline-outline" size="64" color="grey-lighten-2"/>
                        <p class="text-body-1 text-medium-emphasis mt-3">No activity in the last 14 days</p>
                    </div>

                    <div v-if="feedItems.length || liveTrips.length" class="pa-4">
                        <!-- NOW section: active in-progress trips -->
                        <template v-if="liveTrips.length">
                            <div class="d-flex align-center ga-2 mb-2">
                                <v-chip size="x-small" color="success" variant="flat"
                                        prepend-icon="mdi-circle" class="font-weight-black text-uppercase"
                                        style="letter-spacing: 0.05em;">NOW</v-chip>
                                <span class="text-caption text-medium-emphasis">Active trips</span>
                            </div>
                            <ActivityTripCard v-for="trip in liveTrips" :key="'live-' + trip.id"
                                              :trip="trip" :dwell-minutes="0"/>
                            <v-divider class="mt-2 mb-4"/>
                        </template>

                        <!-- Day-grouped merged timeline -->
                        <template v-for="group in groupedFeedItems" :key="group.key">
                            <div class="text-caption font-weight-bold text-medium-emphasis text-uppercase letter-spacing-wide mb-2 mt-4 first-group">
                                {{ group.label }}
                                <span class="ms-1">({{ group.items.length }})</span>
                            </div>
                            <template v-for="item in group.items" :key="item._key">
                                <ActivityTripCard v-if="item._kind === 'trip'"
                                                  :trip="item"
                                                  :dwell-minutes="item._dwell_minutes || 0"/>
                                <ActivityAlertCard v-else
                                                   :notification="item"
                                                   :now-tick="nowTick"
                                                   :address="alertAddressOf(item)"
                                                   :is-new="newIds.has(item.id)"
                                                   @toggle-read="toggleRead"/>
                            </template>
                        </template>
                        <div v-if="feedItemsWithDwell.length > feedShown" class="d-flex justify-center pt-2">
                            <v-btn variant="tonal" color="primary" size="small" @click="feedShown += FEED_PAGE">
                                Show more
                            </v-btn>
                        </div>
                        <div v-else-if="feedTripsCut || feedAlertsCut" class="text-caption text-medium-emphasis text-center pt-3">
                            <template v-if="feedTripsCut">Some older trips or vehicles are not shown.</template>
                            <template v-if="feedAlertsFailed"> Alerts could not be loaded. Use Refresh to try again.</template>
                            <template v-else-if="feedAlertsCut"> Some older alerts are not shown.</template>
                        </div>
                    </div>
                </v-window-item>
            </v-window>
        </v-card>

        <AlertsBulkConfirm :model-value="!!bulkAction" :action="bulkAction || 'read'" :loading="bulkBusy"
                           :vehicle-name="bulkVehicleName" :ignored-filters="tab === 'alerts' && activeTypeFilter !== '' ? 'alert type' : null"
                           @update:model-value="(v) => { if (!v) bulkAction = null; }"
                           @confirm="runBulkAction"/>
    </v-container>
</template>

<script setup>
import {
    ALERT_TYPE,
    ALERT_TYPE_FILTER_OPTIONS,
    alertColor,
    alertHeadline,
    alertIcon,
    alertSeverity,
    payloadLatLng,
    rowTypeOf,
    severityRank,
} from '@/composables/alertTypes';
import {ref, computed, onMounted, onUnmounted, watch} from 'vue';
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {send, unreadNotificationsCount} from "@/composables/apiClient";
import {useAutoMarkRead} from "@/composables/useAutoMarkRead";
import {geocodeLatLng} from "@/composables/useGeocode";
import {companyDayKey, parseApiDate, shiftDayKey} from "@/composables/useTimezone";
import ActivityAlertCard from "@/components/cards/ActivityAlertCard.vue";
import ActivityTripCard from "@/components/cards/ActivityTripCard.vue";
import FeedSummaryCard from "@/components/cards/FeedSummaryCard.vue";
import AlertSeverityChip from "@/components/cards/AlertSeverityChip.vue";
import AlertsBulkConfirm from "@/components/dialogs/assets/AlertsBulkConfirm.vue";

const authStore = useAuthStore();
const {showSnackbar} = useSnackbarStore();

const tab = ref('alerts');
const alerts = ref([]);
const assets = ref([]);
const loading = ref(false);
const loadingMore = ref(false);
const currentPage = ref(1);
const lastPage = ref(1);
const activeTypeFilter = ref('');
const activeAssetFilter = ref('');
const attentionOnly = ref(false);
const bulkAction = ref(null);
const bulkBusy = ref(false);

// Feed tab state
const feedTrips = ref([]);
const feedAlerts = ref([]);
const feedLoading = ref(false);
const feedLoaded = ref(false);
const feedError = ref(false);
const feedAlertsFailed = ref(false);
const feedAssetFilter = ref('');
// Part of the 14 days did not load, so the summary and the end of the timeline say so
const feedTripsCut = ref(false);
const feedAlertsCut = ref(false);
const assetsTotal = ref(0);
// The timeline can reach thousands of cards, so it renders in steps
const FEED_PAGE = 100;
const feedShown = ref(FEED_PAGE);

const nowTick = ref(Date.now());
const alertAddresses = ref({});

// Own alerts that were unread when they loaded. They keep a "New" tint after
// the page marks them read, until the page is reloaded.
const newIds = ref(new Set());

let fetchRequestId = 0;
let feedFetchId = 0;
let nowInterval = null;
let liveRefreshId = 0;
let feedRunId = 0;
let feedAlertsFetchId = 0;

const FEED_DAYS = 14;
const DAY_MS = 24 * 60 * 60 * 1000;
// The API caps trips at 50 a page. One load stays well inside the 200 requests a minute per user.
const FEED_TRIPS_PER_PAGE = 50;
const FEED_TRIP_PAGES_PER_VEHICLE = 6;
const FEED_TRIP_REQUESTS = 120;
const FEED_CONCURRENCY = 6;
const FEED_ALERT_PAGES = 15;

// 4, 11 and 12 never fire, so they get no chip.
const ALL_TYPES = ALERT_TYPE_FILTER_OPTIONS;

const hasMore = computed(() => currentPage.value < lastPage.value);

const isReadRow = (a) => a.read === true || a.read === 1 || a.is_read === true || a.is_read === 1;
const isOwn = (a) => a.user_id != null && String(a.user_id) === String(authStore.user?.id);
// This feed is company scoped and carries colleagues' rows too. Their read
// flag is theirs, so only the viewer's own rows count as unread here.
const isOwnUnread = (a) => isOwn(a) && !isReadRow(a);

// The same number as the bell in the top bar, which every API response updates.
const unreadCount = unreadNotificationsCount;

// The company feed has one row per recipient. Show each alert once, keeping the
// viewer's own row so read and unread still work on it.
const dedupe = (list) => {
    const byAlert = new Map();
    for (const a of list) {
        const key = a.asset_alert_id != null ? `a${a.asset_alert_id}` : `n${a.id}`;
        const seen = byAlert.get(key);
        if (!seen || (!isOwn(seen) && isOwn(a))) byAlert.set(key, a);
    }
    return [...byAlert.values()];
};
const uniqueAlerts = computed(() => dedupe(alerts.value));

const markOwnRead = (list) => list.map(a => isOwnUnread(a) ? {...a, read: true, is_read: true} : a);

// Opening the Alerts tab reads the viewer's alerts. POST notifications/read
// marks only the caller's rows, so only own rows flip. Admin roles get a no-op
// from the API and are skipped.
const {attempt: autoMarkRead} = useAutoMarkRead({
    enabled: () => authStore.user?.type !== 'admin' && tab.value === 'alerts',
    hasUnread: () => alerts.value.some(isOwnUnread),
    onMarked: () => {
        const fresh = new Set(newIds.value);
        alerts.value.forEach(a => { if (isOwnUnread(a)) fresh.add(a.id); });
        newIds.value = fresh;
        alerts.value = markOwnRead(alerts.value);
        attentionAlerts.value = markOwnRead(attentionAlerts.value);
        feedAlerts.value = markOwnRead(feedAlerts.value);
    },
});

const tsOf = (a) => parseApiDate(a?.event_timestamp)?.getTime() || 0;

const agoOf = (a) => {
    const ms = tsOf(a);
    if (!ms) return '';
    const m = Math.floor((nowTick.value - ms) / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
};

// Needs attention: emergencies, urgent alerts and low fuel from the last 48
// hours, worst first. Loaded on its own, with no filters, so it does not
// depend on how many pages of the main list are loaded.
const ATTENTION_WINDOW_MS = 48 * 60 * 60 * 1000;
const attentionAlerts = ref([]);
let attentionFetchId = 0;
const fetchAttention = async () => {
    const reqId = ++attentionFetchId;
    try {
        // Page through the 48 hour window, up to 5 pages, so busy fleets do not drop older alerts
        const from = new Date(Date.now() - ATTENTION_WINDOW_MS).toISOString();
        const rows = [];
        for (let page = 1; page <= 5; page++) {
            const res = await send('get', 'assets/notifications', {scope: 'company', per_page: 100, page, from_date: from});
            if (reqId !== attentionFetchId) return;
            const d = res.data?.data?.notifications ?? res.data?.data;
            rows.push(...(d?.data ?? []));
            if (!d?.meta?.last_page && !d?.last_page) break;
            if (page >= (d.meta?.last_page ?? d.last_page)) break;
        }
        attentionAlerts.value = rows;
    } catch {
        // the strip is optional; the list below still loads
    }
};
const attentionItems = computed(() => dedupe(attentionAlerts.value)
    .filter(a => (alertSeverity(a) || rowTypeOf(a) === ALERT_TYPE.FUEL_LOW)
        && tsOf(a) && nowTick.value - tsOf(a) <= ATTENTION_WINDOW_MS)
    .sort((a, b) => severityRank(a) - severityRank(b) || tsOf(b) - tsOf(a)));

const displayedAlerts = computed(() => (attentionOnly.value ? attentionItems.value : uniqueAlerts.value));

// Day labels and keys follow the company timezone, like the times on each card.
const dayLabel = (key) => {
    const today = companyDayKey(new Date(nowTick.value));
    if (key === today) return 'Today';
    if (key === shiftDayKey(today, -1)) return 'Yesterday';
    const [y, m, d] = key.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d, 12))
        .toLocaleDateString('en-US', {weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC'});
};

// Groups rows by company day, newest day first; `itemOrder` sorts within a day.
const groupByDay = (items, tsFn, itemOrder) => {
    const byKey = new Map();
    for (const item of items) {
        const ts = tsFn(item);
        if (!ts) continue;
        const k = companyDayKey(new Date(ts));
        if (!byKey.has(k)) byKey.set(k, []);
        byKey.get(k).push(item);
    }
    return [...byKey.keys()].sort().reverse().map(k => ({
        key: k,
        label: dayLabel(k),
        items: byKey.get(k).sort(itemOrder),
    }));
};

// Within a day: emergencies first, then urgent, then newest.
const groupedAlerts = computed(() => groupByDay(displayedAlerts.value, tsOf,
    (a, b) => severityRank(a) - severityRank(b) || tsOf(b) - tsOf(a)));

const tripTs = (t) => parseApiDate(t?.time)?.getTime() || 0;

// The Feed tab's vehicle chip applies to the summary and the timeline alike
const assetFeedTrips = computed(() => feedTrips.value
    .filter(t => !feedAssetFilter.value || t.asset_id === feedAssetFilter.value));
const assetFeedAlerts = computed(() => dedupe(feedAlerts.value)
    .filter(a => !feedAssetFilter.value || a.asset_id === feedAssetFilter.value));

// Feed tab: trips still running. end_lat is written from the first packet, so the trip row
// cannot tell; the vehicle's current trip while it is in motion can.
const liveTrips = computed(() => assetFeedTrips.value.filter(t => t._live));

// Feed tab: merged trips + alerts, sorted desc by timestamp
const feedItems = computed(() => {
    const fourteenDaysAgo = Date.now() - FEED_DAYS * DAY_MS;
    const filteredTrips = assetFeedTrips.value
        .filter(t => !t._live)
        .filter(t => tripTs(t) >= fourteenDaysAgo)
        .map(t => ({...t, _kind: 'trip', _ts: tripTs(t), _key: `t-${t.id}`}));

    const filteredAlerts = assetFeedAlerts.value
        .filter(a => tsOf(a) >= fourteenDaysAgo)
        .map(a => ({...a, _kind: 'alert', _ts: tsOf(a), _key: `a-${a.id}`}));

    return [...filteredTrips, ...filteredAlerts].sort((a, b) => b._ts - a._ts);
});

// Dwell computation: inject _dwell_minutes into trips (time parked before this trip)
const feedItemsWithDwell = computed(() => {
    // Group trips by asset_id sorted asc by time for consecutive-trip dwell calc
    const tripsByAsset = {};
    for (const item of feedItems.value) {
        if (item._kind !== 'trip') continue;
        const aid = item.asset_id || 'unknown';
        if (!tripsByAsset[aid]) tripsByAsset[aid] = [];
        tripsByAsset[aid].push(item);
    }
    // For each asset, compute end time of each trip and dwell to next trip start
    // tripsByAsset[aid] is sorted desc by _ts; we need asc order for consecutive pair check
    const dwellMap = {};
    for (const [, trips] of Object.entries(tripsByAsset)) {
        const asc = [...trips].sort((a, b) => a._ts - b._ts);
        for (let i = 1; i < asc.length; i++) {
            const prev = asc[i - 1];
            const curr = asc[i];
            if (!prev.duration) continue;
            const prevEnd = prev._ts + (parseInt(prev.duration) || 0) * 1000;
            const dwellMs = curr._ts - prevEnd;
            const dwellMin = Math.round(dwellMs / 60000);
            if (dwellMin >= 5 && dwellMin <= 24 * 60) {
                dwellMap[curr._key] = dwellMin;
            }
        }
    }
    return feedItems.value.map(item => ({
        ...item,
        _dwell_minutes: dwellMap[item._key] || 0,
    }));
});

const visibleFeedItems = computed(() => feedItemsWithDwell.value.slice(0, feedShown.value));
// Failed alerts only stand in for the timeline when there is nothing else to show
const feedFailure = computed(() => {
    if (feedError.value) return 'Trips could not be loaded.';
    return feedAlertsFailed.value && !feedItems.value.length && !liveTrips.value.length ? 'Alerts could not be loaded.' : '';
});
const groupedFeedItems = computed(() => groupByDay(visibleFeedItems.value, (i) => i._ts, (a, b) => b._ts - a._ts));

const isLiveTrip = (trip, asset) => !!asset?.moving && String(asset.tripId) === String(trip.id);
const feedTrip = (trip, asset) => ({
    ...trip, asset_id: asset.id, asset: asset.name, _company: asset.company, _live: isLiveTrip(trip, asset),
});
const feedStartDate = () => companyDayKey(new Date(Date.now() - FEED_DAYS * DAY_MS));
const newestFirst = (trips) => trips.sort((a, b) => tripTs(b) - tripTs(a));

// Only vehicles that can have trips in the window are asked, the most recently reporting first and
// a few requests at a time. Later pages queue behind every vehicle's first page, so a busy truck
// cannot use up the request budget of the others.
const fetchFeedTrips = async () => {
    const reqId = ++feedFetchId;
    const windowStart = Date.now() - FEED_DAYS * DAY_MS;
    const reportedAt = (a) => parseApiDate(a.lastUpdated)?.getTime() || 0;
    const queue = assets.value
        .filter(a => reportedAt(a) >= windowStart)
        .sort((a, b) => reportedAt(b) - reportedAt(a))
        .map(asset => ({asset, page: 1}));
    const startDate = feedStartDate();

    // Vehicles past the first page of assets, and one whose tracker has just been moved off it, are
    // not asked but can have trips in the window
    let cut = assetsTotal.value > assets.value.length || assets.value.some(a => !a.hasDevice
        && (parseApiDate(a.updatedAt)?.getTime() || 0) >= windowStart);
    let requests = 0;
    let failed = 0;
    const byId = new Map();
    const worker = async () => {
        while (queue.length && reqId === feedFetchId) {
            if (requests >= FEED_TRIP_REQUESTS) {
                cut = true;
                return;
            }
            const {asset, page} = queue.shift();
            requests++;
            try {
                const r = await send('get', `assets/${asset.id}/trips`, {page, per_page: FEED_TRIPS_PER_PAGE, start_date: startDate});
                (r.data?.data?.data ?? []).forEach(t => byId.set(t.id, feedTrip(t, asset)));
                if (page < (r.data?.data?.meta?.last_page ?? 1)) {
                    if (page < FEED_TRIP_PAGES_PER_VEHICLE) queue.push({asset, page: page + 1});
                    else cut = true;
                }
            } catch {
                failed++;
                cut = true;
            }
        }
    };
    await Promise.all(Array.from({length: FEED_CONCURRENCY}, worker));

    if (reqId !== feedFetchId) return;
    feedTrips.value = newestFirst([...byId.values()]);
    feedTripsCut.value = cut;
    feedError.value = failed > 0 && !byId.size;
};

// The same 14 days as the trips, on its own so the Alerts tab's filters and paging do not touch it
const fetchFeedAlerts = async () => {
    const reqId = ++feedAlertsFetchId;
    const from = new Date(Date.now() - FEED_DAYS * DAY_MS).toISOString();
    const rows = [];
    let cut = false;
    let failed = false;
    try {
        for (let page = 1; page <= FEED_ALERT_PAGES; page++) {
            const res = await send('get', 'assets/notifications', {scope: 'company', per_page: 100, page, from_date: from});
            if (reqId !== feedAlertsFetchId) return;
            const d = res.data?.data?.notifications ?? res.data?.data;
            rows.push(...(d?.data ?? []));
            const last = d?.meta?.last_page ?? 1;
            if (page >= last) break;
            cut = page === FEED_ALERT_PAGES;
        }
    } catch {
        if (reqId !== feedAlertsFetchId) return;
        cut = true;
        failed = true;
        showSnackbar('Could not load alerts', 'error');
    }
    // A failed refresh keeps what was already loaded
    if (!failed || rows.length) feedAlerts.value = rows;
    feedAlertsCut.value = cut;
    feedAlertsFailed.value = failed;
};

// Answers whether the list loaded
const fetchAssets = async () => {
    try {
        const res = await send('get', 'assets', {per_page: 100});
        const page = res.data?.data?.assets;
        assetsTotal.value = page?.total ?? 0;
        // By name: the API sorts by last report, which would shuffle the chips on every refresh
        assets.value = (page?.data ?? []).map(a => ({
            id: a.id,
            name: a.label || a.name || `Vehicle ${a.id}`,
            company: a.company_id ?? a.company,
            lastUpdated: a.last_updated,
            updatedAt: a.updated_at,
            hasDevice: !!a.device_id,
            tripId: a.trip_id,
            moving: a.fleet_status === 'moving' || a.fleet_status === 'idling',
        })).sort((a, b) => String(a.name).localeCompare(String(b.name)));
        return true;
    } catch {
        return false;
    }
};

const refreshFeed = async () => {
    const run = ++feedRunId;
    feedLoading.value = true;
    const assetsLoaded = await fetchAssets();
    if (run !== feedRunId) return;
    await Promise.all([fetchFeedTrips(), fetchFeedAlerts()]);
    if (run !== feedRunId) return;
    if (!assetsLoaded && !assets.value.length) feedError.value = true;
    feedLoaded.value = true;
    feedLoading.value = false;
};

// Moves trips in and out of NOW while the Feed is on screen. A vehicle on a trip the feed has not
// seen, or whose live trip has ended, gets its newest page again for the trip's final figures.
const refreshLive = async () => {
    if (document.hidden || tab.value !== 'feed' || !feedLoaded.value || feedLoading.value) return;
    const id = ++liveRefreshId;
    const feedId = feedFetchId;
    const liveBefore = new Map(feedTrips.value.filter(t => t._live).map(t => [t.asset_id, t.id]));
    if (!await fetchAssets() || id !== liveRefreshId || feedId !== feedFetchId) return;

    const known = new Set(feedTrips.value.map(t => String(t.id)));
    const changed = assets.value.filter((a) => {
        const ended = liveBefore.has(a.id) && !(a.moving && String(a.tripId) === String(liveBefore.get(a.id)));
        return ended || (a.moving && a.tripId && !known.has(String(a.tripId)));
    }).slice(0, FEED_CONCURRENCY);
    const byId = new Map(feedTrips.value.map(t => [t.id, t]));
    for (const asset of changed) {
        try {
            const r = await send('get', `assets/${asset.id}/trips`, {page: 1, per_page: FEED_TRIPS_PER_PAGE, start_date: feedStartDate()});
            if (id !== liveRefreshId || feedId !== feedFetchId) return;
            (r.data?.data?.data ?? []).forEach(t => byId.set(t.id, feedTrip(t, asset)));
        } catch {
            // the next tick tries again
        }
    }
    const assetById = new Map(assets.value.map(a => [a.id, a]));
    feedTrips.value = newestFirst([...byId.values()].map(t => ({...t, _live: isLiveTrip(t, assetById.get(t.asset_id))})));
};

const fetchAlerts = async (page = 1, append = false) => {
    const requestId = ++fetchRequestId;
    if (append) loadingMore.value = true;
    else loading.value = true;

    const params = { page, scope: 'company' };
    if (activeTypeFilter.value !== '') params.alert_type = activeTypeFilter.value;
    if (activeAssetFilter.value !== '') params.asset = activeAssetFilter.value;

    try {
        const res = await send('get', 'assets/notifications', params);
        if (requestId !== fetchRequestId) return;
        const d = res.data?.data;
        // API returns different shapes depending on whether asset filter is active:
        // asset-filtered: { notifications: { data, meta } }; global: { data, meta }
        const items = d?.notifications?.data ?? d?.data ?? [];
        const meta = d?.notifications?.meta ?? d?.meta;
        if (append) {
            const seen = new Set(alerts.value.map(a => a.id));
            const fresh = items.filter(a => !seen.has(a.id));
            alerts.value.push(...fresh);
            resolveAlertAddresses(fresh);
        } else {
            alerts.value = items;
        }
        currentPage.value = meta?.current_page ?? page;
        lastPage.value = meta?.last_page ?? 1;
        // The list the user is now looking at has loaded (open, refresh,
        // filter, reload, or a further page with unread own rows).
        autoMarkRead();
    } catch {
        if (requestId !== fetchRequestId) return;
        showSnackbar('Could not load alerts', 'error');
    } finally {
        if (requestId === fetchRequestId) {
            loading.value = false;
            loadingMore.value = false;
        }
    }
};

const loadMore = () => {
    if (!hasMore.value || loadingMore.value) return;
    fetchAlerts(currentPage.value + 1, true);
};

const refresh = () => {
    fetchAttention();
    return fetchAlerts(1);
};

const clearFilters = () => {
    activeTypeFilter.value = '';
    activeAssetFilter.value = '';
    attentionOnly.value = false;
};

const addrKey = (lat, lng) => `${(+lat).toFixed(5)},${(+lng).toFixed(5)}`;
const alertAddressOf = (item) => {
    const c = payloadLatLng(item?.payload);
    if (!c) return null;
    return alertAddresses.value[addrKey(c.lat, c.lng)] || null;
};
const resolveAlertAddresses = (list) => {
    for (const item of list) {
        const c = payloadLatLng(item?.payload);
        if (!c) continue;
        const {lat, lng} = c;
        const k = addrKey(lat, lng);
        if (k in alertAddresses.value) continue;
        alertAddresses.value[k] = null;
        geocodeLatLng({lat, lon: lng}).then((addr) => {
            if (addr) alertAddresses.value = {...alertAddresses.value, [k]: addr};
        }).catch(() => {});
    }
};
watch(alerts, (list) => resolveAlertAddresses(list || []), {immediate: true});
watch(attentionAlerts, (list) => resolveAlertAddresses(list || []));
// The Feed can hold 14 days of alerts, so only the ones on screen are looked up
watch(visibleFeedItems, (list) => resolveAlertAddresses(list.filter(i => i._kind === 'alert')));

const toggleRead = async (notification) => {
    try {
        await send('put', `notifications/${notification.id}`);
        const wasRead = isReadRow(notification);
        const flip = (list) => list.map(a => a.id === notification.id ? {...a, read: !wasRead, is_read: !wasRead} : a);
        alerts.value = flip(alerts.value);
        attentionAlerts.value = flip(attentionAlerts.value);
        feedAlerts.value = flip(feedAlerts.value);
    } catch {
        showSnackbar('Could not update notification', 'error');
    }
};

// The open tab's vehicle chip limits both bulk calls; the API cannot apply the type chip.
const bulkAssetId = computed(() => (tab.value === 'feed' ? feedAssetFilter.value : activeAssetFilter.value));
const bulkVehicleName = computed(() => (bulkAssetId.value !== ''
    ? (assets.value.find(x => x.id === bulkAssetId.value)?.name || 'this vehicle')
    : null));

// Both calls act on the viewer's own alerts only.
const runBulkAction = async () => {
    const action = bulkAction.value;
    const assetId = bulkAssetId.value !== '' ? bulkAssetId.value : undefined;
    bulkBusy.value = true;
    try {
        if (action === 'clear') {
            await send('delete', 'notifications/clear', {params: {asset_id: assetId}});
            showSnackbar('Your alerts were cleared');
        } else {
            await send('post', 'notifications/read', {asset_id: assetId});
            showSnackbar('Alerts marked as read');
        }
        refresh();
        if (feedLoaded.value) fetchFeedAlerts();
        bulkAction.value = null;
    } catch {
        showSnackbar(action === 'clear' ? 'Could not clear alerts' : 'Could not mark all as read', 'error');
    } finally {
        bulkBusy.value = false;
    }
};

const onCompanyReload = () => {
    if (tab.value === 'alerts') refresh();
    if (tab.value === 'feed') refreshFeed();
};

onMounted(async () => {
    window.addEventListener('company-data-reload', onCompanyReload);
    nowInterval = setInterval(() => {
        nowTick.value = Date.now();
        refreshLive();
    }, 60000);
    fetchAttention();
    await fetchAlerts(1);
    fetchAssets();
});

// Load the feed lazily when its tab is first opened, with fresh vehicles so NOW is right from the
// start; a fleet with no trips is not asked again.
watch(tab, (t) => {
    if (t === 'feed' && !feedLoaded.value && !feedLoading.value) refreshFeed();
});

watch(feedAssetFilter, () => {
    feedShown.value = FEED_PAGE;
});

onUnmounted(() => {
    window.removeEventListener('company-data-reload', onCompanyReload);
    if (nowInterval) clearInterval(nowInterval);
    fetchRequestId++;
    feedFetchId++;
    feedAlertsFetchId++;
    feedRunId++;
    liveRefreshId++;
});

watch([activeTypeFilter, activeAssetFilter], () => {
    alerts.value = [];
    currentPage.value = 1;
    attentionOnly.value = false;
    fetchAlerts(1);
});
</script>

<style scoped>
.letter-spacing-wide {
    letter-spacing: 0.08em;
}
.first-group:first-child {
    margin-top: 0 !important;
}
.min-w-0 {
    min-width: 0;
}
.attention-strip {
    background-color: rgba(var(--v-theme-error), 0.06);
    border-color: rgba(var(--v-theme-error), 0.35);
}
</style>
